"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useState, useCallback } from "react";
import { X, ChevronLeft, ChevronRight, ImageOff } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { useScrollLock } from "@/hooks/useScrollLock";
import type { FeaturedGalleryItem } from "@/lib/gallery-types";

/** Image with built-in skeleton loading */
function FeaturedImage({
  src,
  alt,
  className = "",
}: {
  src: string;
  alt: string;
  className?: string;
}) {
  // Drop the manual `loaded` opacity-fade. It deferred visibility past
  // image decode (~50-150ms perceived latency) for no benefit — the
  // Skeleton beneath already provides a placeholder, and the Image
  // replaces it natively as soon as it decodes. Same anti-pattern that
  // PageHero already removed.
  const [error, setError] = useState(false);

  if (!src || error) {
    return (
      <div className="relative flex aspect-square flex-col items-center justify-center gap-2 overflow-hidden rounded-2xl bg-muted text-muted-foreground animate-fade-in">
        <ImageOff className="h-6 w-6 opacity-40" />
        <span className="text-xs font-medium opacity-60">Image unavailable</span>
      </div>
    );
  }

  return (
    <AspectRatio ratio={1} className="relative overflow-hidden rounded-2xl">
      <Skeleton className="absolute inset-0 rounded-2xl" />
      <Image
        src={src}
        alt={alt}
        width={600}
        height={600}
        loading="lazy"
        // Let next/image emit a srcset so the browser picks a narrower
        // descriptor for this below-the-fold grid (mobile 50vw, tablet
        // 33vw, capped at the 600px render width on desktop). R2 URLs are
        // in next.config remotePatterns; the Workers optimizer passes them
        // through without re-encoding, so this is a pure download win — no
        // `unoptimized` so the descriptor set is actually generated.
        sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 600px"
        onError={() => setError(true)}
        className={`relative z-10 h-full w-full object-cover ${className}`}
      />
    </AspectRatio>
  );
}

/** Lightbox image with skeleton loading */
function LightboxImage({ src, alt }: { src: string; alt: string }) {
  // Same fix as FeaturedImage — no manual onLoad/opacity gating.
  const [error, setError] = useState(false);

  if (!src || error) {
    return (
      <div className="flex min-h-50 min-w-50 flex-col items-center justify-center gap-3 rounded-lg bg-white/5 text-white/50 animate-fade-in">
        <ImageOff className="h-8 w-8 opacity-50" />
        <span className="text-sm font-medium">Image unavailable</span>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-50 min-w-50 max-h-[85vh] max-w-[90vw] items-center justify-center">
      <Skeleton className="absolute min-h-50 min-w-50 rounded-lg" />
      <Image
        src={src}
        alt={alt}
        width={1200}
        height={900}
        sizes="90vw"
        unoptimized
        onError={() => setError(true)}
        className="relative z-10 max-h-[85vh] max-w-full rounded-lg object-contain"
      />
    </div>
  );
}

/**
 * The seed shape the home page hands down (`FeaturedGalleryItem`), plus the one
 * field only the client fetch path supplies.
 *
 * `imageUrlRaw` is the direct CDN URL. It is optional and NOT part of the seed:
 * `/api/gallery` returns it, `selectFeaturedGalleryItems` does not, and every
 * read here is `item.imageUrlRaw || item.imageUrl`. Extending the shared parent
 * rather than re-declaring it means a field added to the seed reaches this
 * component instead of being silently dropped at the prop boundary.
 */
interface GalleryItem extends FeaturedGalleryItem {
  imageUrlRaw?: string;
}

/** Max images to fetch/rotate from (mobile shows 4, tablet+ shows 3) */
const POOL_COUNT = 6;

/** Mobile < 768: 4 images. Tablet/desktop: 3 images. */
function useFeaturedDisplayCount(): number {
  const [count, setCount] = useState(4);
  useEffect(() => {
    const mql = window.matchMedia("(min-width: 768px)");
    const update = () => setCount(mql.matches ? 3 : 4);
    update();
    mql.addEventListener("change", update);
    return () => mql.removeEventListener("change", update);
  }, []);
  return count;
}
/** Seconds between rotations */
const ROTATE_INTERVAL = 8;

/**
 * Randomly pick `count` items from an array (Fisher-Yates shuffle slice).
 * Returns a new array — never mutates the input.
 */
function pickRandom<T>(items: T[], count: number): T[] {
  if (items.length <= count) return [...items];
  const pool = [...items];
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return pool.slice(0, count);
}

export function FeaturedGallery({ initialItems }: { initialItems?: GalleryItem[] } = {}) {
  const displayCount = useFeaturedDisplayCount();
  const hasInitial = initialItems && initialItems.length > 0;
  const [allImages, setAllImages] = useState<GalleryItem[]>(hasInitial ? initialItems : []);
  // First paint MUST be deterministic. pickRandom() uses Math.random(), so
  // shuffling in this initializer renders a different image order on the server
  // than on the client and trips a hydration mismatch (the section is SSR-ed for
  // alt-text/SEO). Seed with a stable slice; randomise once after mount below.
  const [displayed, setDisplayed] = useState<GalleryItem[]>(
    hasInitial ? initialItems.slice(0, POOL_COUNT) : []
  );
  const [loading, setLoading] = useState(!hasInitial);
  const [lightboxItem, setLightboxItem] = useState<GalleryItem | null>(null);

  const closeLightbox = useCallback(() => {
    setLightboxItem(null);
  }, []);

  // Fetch gallery images on mount — skip if server-side data was provided
  useEffect(() => {
    if (hasInitial) return;
    fetch("/api/gallery")
      .then((r) => r.json() as Promise<{ items?: GalleryItem[] }>)
      .then((data) => {
        const images = (data.items ?? []).filter(
          (item) => item.mediaType === "image" && item.imageUrl
        );
        setAllImages(images);
        setDisplayed(pickRandom(images, POOL_COUNT));
      })
      .catch((e) => {
        console.warn("[FeaturedGallery] Failed to load gallery:", e);
        setAllImages([]);
        setDisplayed([]);
      })
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Randomise the server-seeded set once, after hydration. Safe here (unlike the
  // useState initializer) because a post-mount state change never has to match
  // the SSR HTML. Skipped without initial items — the fetch path shuffles its own.
  useEffect(() => {
    if (!initialItems || initialItems.length === 0) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- the server seed is intentionally randomised only after hydration.
    setDisplayed(pickRandom(initialItems, POOL_COUNT));
  }, [initialItems]);

  // Rotate displayed images periodically
  const rotate = useCallback(() => {
    if (allImages.length <= POOL_COUNT) return;
    setDisplayed((prev) => {
      const prevIds = new Set(prev.map((p) => p.id));
      const others = allImages.filter((img) => !prevIds.has(img.id));
      if (others.length >= POOL_COUNT) {
        return pickRandom(others, POOL_COUNT);
      }
      return pickRandom(allImages, POOL_COUNT);
    });
  }, [allImages]);

  useEffect(() => {
    if (allImages.length <= POOL_COUNT) return;
    const timer = setInterval(rotate, ROTATE_INTERVAL * 1000);
    return () => clearInterval(timer);
  }, [allImages, rotate]);

  // Lightbox navigation
  const lightboxIndex = lightboxItem
    ? allImages.findIndex((img) => img.id === lightboxItem.id)
    : -1;

  const goNext = useCallback(() => {
    if (lightboxIndex < 0 || allImages.length === 0) return;
    setLightboxItem(allImages[(lightboxIndex + 1) % allImages.length]);
  }, [lightboxIndex, allImages]);

  const goPrev = useCallback(() => {
    if (lightboxIndex < 0 || allImages.length === 0) return;
    setLightboxItem(
      allImages[(lightboxIndex - 1 + allImages.length) % allImages.length]
    );
  }, [lightboxIndex, allImages]);

  // Keyboard for lightbox (Dialog handles scroll lock)
  useEffect(() => {
    if (!lightboxItem) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") closeLightbox();
      else if (e.key === "ArrowRight") goNext();
      else if (e.key === "ArrowLeft") goPrev();
    }
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [lightboxItem, closeLightbox, goNext, goPrev]);

  // Lock page scrolling while full-screen media is open. data-lightbox-open removes scrollbar gutter to prevent white strip.
  useScrollLock(lightboxItem !== null);

  if (loading) {
    return (
      <div className="grid grid-cols-2 gap-5 md:grid-cols-3 md:gap-6">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="aspect-square rounded-xl" />
        ))}
      </div>
    );
  }

  if (displayed.length === 0) {
    return (
      <div className="flex flex-col items-center gap-4 py-12 text-center">
        <p className="font-serif text-lg text-muted-foreground">
          New arrangements coming soon
        </p>
        <p className="max-w-md text-sm text-muted-foreground">
          We&apos;re always creating new bespoke arrangements. Browse our full gallery or get in touch with your idea.
        </p>
        <div className="mt-2 flex gap-3">
          <Button asChild size="sm">
            <Link href="/gallery">View gallery</Link>
          </Button>
          <Button asChild variant="outline" size="sm">
            <Link href="/enquire">Enquire</Link>
          </Button>
        </div>
      </div>
    );
  }

  const visible = displayed.slice(0, displayCount);

  return (
    <>
      <div className="grid grid-cols-2 gap-5 md:grid-cols-3 md:gap-6">
        {visible.map((item) => (
          <div
            key={item.id}
            className="animate-fade-in-scale"
          >
            <Button
              type="button"
              variant="ghost"
              onClick={() => setLightboxItem(item)}
              // HOMEA11Y-1: the button's only child is an image, so its
              // accessible name came from that image's `alt` — and in the
              // states where the image isn't there yet (the Skeleton beneath
              // it, or the "Image unavailable" fallback after an onError)
              // there was NO name at all. These were the only 4 unlabelled
              // buttons in the site's accessibility tree. An explicit label
              // also says what the control DOES, which an alt text can't.
              aria-label={
                item.title
                  ? `View ${item.title} full screen`
                  : "View floral arrangement full screen"
              }
              className="group block h-auto w-full cursor-pointer overflow-hidden rounded-xl bg-muted p-0 hover:bg-muted"
            >
              <FeaturedImage
                src={item.imageUrlRaw || item.imageUrl}
                alt={item.title || "Floral arrangement by The Floral Muse"}
                className="transition-transform duration-500 group-hover:scale-105"
              />
            </Button>
          </div>
        ))}
      </div>

      <Dialog open={!!lightboxItem} onOpenChange={(open) => !open && closeLightbox()}>
        <DialogContent
          showCloseButton={false}
          className="left-0! top-0! z-100! h-full! max-h-none! w-full! max-w-none! overflow-hidden! translate-x-0! translate-y-0! rounded-none! border-0! bg-black! p-0! data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          onClick={closeLightbox}
        >
          <DialogTitle className="sr-only">
            {lightboxItem ? lightboxItem.title || "Floral arrangement" : ""}
          </DialogTitle>
          <DialogDescription className="sr-only">Full-screen image preview</DialogDescription>
          {/* Full-viewport wrapper so media is always centered — matches /gallery and /products */}
          <div className="fixed inset-0 flex flex-col items-center justify-center overflow-hidden pt-[max(1rem,env(safe-area-inset-top))] pb-[max(1rem,env(safe-area-inset-bottom))] pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))]">
          <Button
            variant="ghost"
            size="icon"
            onClick={closeLightbox}
            className="absolute right-3 top-3 z-10 flex min-h-11 min-w-11 size-11 items-center justify-center rounded-md bg-white/10 text-white/80 hover:bg-white/20 hover:text-white md:size-9 [&_svg]:size-4"
            aria-label="Close"
          >
            <X />
          </Button>

          {lightboxItem && allImages.length > 1 && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                goPrev();
              }}
              onPointerDown={(e) => {
                e.stopPropagation();
                goPrev();
              }}
              className="touch-manipulation absolute left-4 top-1/2 z-10 flex min-h-11 min-w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 p-2 text-white/80 hover:bg-white/20 hover:text-white md:min-h-0 md:min-w-0"
              aria-label="Previous"
            >
              <ChevronLeft className="h-6 w-6" />
            </Button>
          )}

          {lightboxItem && (
            <div
              className="relative flex max-h-[85vh] max-w-[90vw] shrink-0 flex-col items-center justify-center animate-fade-in-scale-lightbox"
              onClick={(e) => e.stopPropagation()}
            >
              <LightboxImage
                src={lightboxItem.imageUrlRaw || lightboxItem.imageUrl}
                alt={lightboxItem.title || "Floral arrangement"}
              />
            </div>
          )}

          {lightboxItem && allImages.length > 1 && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                goNext();
              }}
              onPointerDown={(e) => {
                e.stopPropagation();
                goNext();
              }}
              className="touch-manipulation absolute right-4 top-1/2 z-10 flex min-h-11 min-w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 p-2 text-white/80 hover:bg-white/20 hover:text-white md:min-h-0 md:min-w-0"
              aria-label="Next"
            >
              <ChevronRight className="h-6 w-6" />
            </Button>
          )}

          {lightboxItem && allImages.length > 1 && (
            <p className="absolute bottom-4 left-1/2 -translate-x-1/2 text-xs text-white/50">
            {lightboxIndex + 1} / {allImages.length}
            </p>
          )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
